import {raise as _raise, delay, getUUID, makeGet} from '@yandex-delivery/utils'
import {
  fetchClearDraftSync,
  fetchConfirmOfferDeprecated,
  fetchCreateAccepted,
  fetchPollSharing,
  fetchRemoveLastOrderId,
  fetchStoreLastOrderId,
} from 'src/shared/api'
import {appConfig, i18n} from 'src/shared/config'
import type {CreatedRequest, Order, SearchedSddClaim} from 'src/shared/types'

import {fromOffersOrder} from './utils/from-offers-order'

const orderGet = makeGet<Order>()
const claimGet = makeGet<SearchedSddClaim>()
const requestGet = makeGet<CreatedRequest>()

const getOfferId = orderGet('offerFlow.offer.id')
const getBusiness = orderGet('isBusiness')
const getClaimId = claimGet('id')

const getRequestId = requestGet('request_id')

function raise(message: string): never {
  _raise(`[create_accept]: ${message}`)
}

function pickRequestId(request: CreatedRequest) {
  return (
    getRequestId(request) ||
    raise('request.id should be a string, but got undefined')
  )
}

function fetchRouteUrl(claim: SearchedSddClaim) {
  return fetchStoreLastOrderId(getClaimId(claim))
    .then(() =>
      fetchPollSharing({
        order_id: getClaimId(claim),
        order_provider_id: 'cargo-claims',
      }),
    )
    .then(sharing => fetchRemoveLastOrderId().then(() => sharing.sharing_url))
}

function fetchRegistrationUrl(claim: SearchedSddClaim): Promise<string> {
  return Promise.resolve(
    appConfig.getRegistrationUrl({claim_id: getClaimId(claim)}),
  )
}

export function fetchCreateOrder(
  order: Order,
  onSuccess?: VoidFunction,
  onError?: VoidFunction,
): Promise<void> {
  const offerId =
    getOfferId(order) || raise('offerId should be a string, but got undefined')
  const fetchUrl = getBusiness(order) ? fetchRegistrationUrl : fetchRouteUrl

  return fetchCreateAccepted(
    {
      offer: offerId,
      claim: fromOffersOrder(i18n, order),
    },
    {
      request_id: getUUID(),
      country: appConfig.countryCode,
    },
  )
    .then(fetchUrl)
    .then(url => {
      fetchClearDraftSync()
      onSuccess?.()
      window.location.href =
        url || raise('url should be a string, but got undefined')
    })
    .then(
      // Оставляем форму в состоянии isSubmitting во время редиректа
      () => delay(10000),
    )
    .catch(() => onError?.())
}

function fetchNDDRouteUrl(request: CreatedRequest) {
  return fetchStoreLastOrderId(pickRequestId(request))
    .then(() =>
      fetchPollSharing({
        order_id: pickRequestId(request),
        order_provider_id: 'united-delivery-platform',
      }),
    )
    .then(sharing => fetchRemoveLastOrderId().then(() => sharing.sharing_url))
}

function fetchNDDRegistrationUrl(request: CreatedRequest): Promise<string> {
  return Promise.resolve(
    appConfig.getRegistrationUrl({
      retpath: `${new URL(appConfig.getAccountUrl(`/ndd/${pickRequestId(request)}`), window.location.origin)}`,
    }),
  )
}

export function fetchCreateNddOrder(offerId: string, isBusiness?: boolean) {
  const fetchUrl = isBusiness ? fetchNDDRegistrationUrl : fetchNDDRouteUrl

  return fetchConfirmOfferDeprecated(offerId)
    .then(fetchUrl)
    .then(async link => {
      fetchClearDraftSync()

      return link
    })
}
